import { useRateManagement } from '@api/useFreight/useFreight'
import type { CreateManualAccessorialRequest, ManualAccessorial, Ratebook } from '@api/useFreight/types'

import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, DatePicker, Divider, Form, Input, InputNumber, Select, Switch, message } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { getFeeTypeUnit, isPercentageFeeType } from '../../../../shared/util/feeTypeUtils'
import { useAccessorialForm } from '../AutomaticAccessorials/hooks/useAccessorialForm'
import { useFreightAssessorials } from '@/api/useFreightAssessorials'
import { FreightLinkingOptionsSelect } from '../../components/FreightLinkingOptionsSelect'

interface CreateAccessorialFormProps {
  ratebook: Ratebook
  onSuccess: () => void
  onCancel: () => void
  editingAccessorial?: ManualAccessorial | null
}

export interface FormValues {
  name: string
  description: string
  fee_type: string
  rate: number
  effective_from: dayjs.Dayjs
  requires_approval: boolean
  requires_reason_code: boolean
  exclude_from_invoice: boolean
  freight_region?: string
  freight_region_id?: string
  product_group: string | null
  link_to_special_pay?: boolean
  linked_special_pay_config?: {
    value: string
    label: string
  }
  link_type?: string | undefined
}

export function CreateAccessorialForm({
  ratebook,
  onSuccess,
  onCancel,
  editingAccessorial,
}: CreateAccessorialFormProps) {
  const { useCreateManualAccessorial, useFilteredMetadataQuery } = useRateManagement()
  const createAccessorialMutation = useCreateManualAccessorial()
  const { specialPayOptions } = useFreightAssessorials()

  // Get metadata for dropdown options
  const { data: metadata } = useFilteredMetadataQuery(ratebook)
  const [form] = Form.useForm<FormValues>()

  // Get the current fee type value from the form
  const currentFeeType = Form.useWatch('fee_type', form)
  const isPercentageType = isPercentageFeeType(currentFeeType)

  // Initialize form with editing data when available
  React.useEffect(() => {
    if (editingAccessorial) {
      // Get current rate
      const currentRate = editingAccessorial.rates[0]?.rate || 0
      const isPercentageType = isPercentageFeeType(editingAccessorial.fee_type.type)
      const isLinkedToSpecialPay = editingAccessorial.linked_special_pay_config.length > 0

      // Set the form values
      form.setFieldsValue({
        name: editingAccessorial.name,
        description: editingAccessorial.description || '',
        fee_type: editingAccessorial.fee_type.type,
        // Convert decimal to percentage for display if it's a percentage type
        rate: isPercentageType ? currentRate * 100 : currentRate,
        effective_from: dayjs(editingAccessorial.rates[0]?.effective_from as string),
        requires_approval: editingAccessorial.requires_approval,
        requires_reason_code: editingAccessorial.requires_reason_code || false,
        exclude_from_invoice: !!editingAccessorial.exclude_from_invoice,
        freight_region: editingAccessorial.freight_region_name,
        freight_region_id: editingAccessorial.freight_region_id,
        product_group: editingAccessorial.product_group,
        link_to_special_pay: isLinkedToSpecialPay,
        linked_special_pay_config: isLinkedToSpecialPay
          ? {
              value: editingAccessorial.linked_special_pay_config[0]?.sp_id || '',
              label: editingAccessorial.linked_special_pay_config[0]?.name || '',
            }
          : undefined,
        link_type: isLinkedToSpecialPay ? editingAccessorial.linked_special_pay_config[0]?.link_type : undefined,
      })
    }
  }, [editingAccessorial, form])

  const onFinish = async (values: FormValues) => {
    try {
      // Transform rate if it's a percentage fee type
      const rate = isPercentageFeeType(values.fee_type) ? Number((values.rate / 100).toFixed(5)) : values.rate
      const isLinkedToSpecialPay = values.link_to_special_pay

      // Structure the payload to match API expectations
      const processedFormData: CreateManualAccessorialRequest = {
        counterparty_id: ratebook.counterparty_id,
        book_type: ratebook.book_type,
        name: editingAccessorial ? editingAccessorial.name : values.name,
        description: values.description,
        fee_type: {
          type: values.fee_type,
          threshold: ['Per Stop', 'Per Drop', 'Per Load', 'Detention', 'Demurrage', 'Out of Route'].includes(
            values.fee_type
          )
            ? 0
            : undefined,
        },
        freight_region_name: values.freight_region,
        freight_region_id: values.freight_region_id,
        product_group: values.product_group === 'Any' ? null : values.product_group,
        requires_approval: values.requires_approval,
        requires_reason_code: values.requires_reason_code,
        exclude_from_invoice: values.exclude_from_invoice,
        destinations: {
          locations: [],
          site_states: [],
          site_regions: [],
          terminal_states: [],
          terminal_regions: [],
        },
        destination_ids: [],
        origins: {
          locations: [],
          site_states: [],
          site_regions: [],
          terminal_states: [],
          terminal_regions: [],
        },
        origin_ids: [],
        ratebook_id: ratebook._id as string,
        rate: rate,
        rates: [
          {
            effective_from: values.effective_from.toISOString(),
            rate,
          },
        ],
        linked_special_pay_config: isLinkedToSpecialPay
          ? [
              {
                sp_id: values.linked_special_pay_config?.value || '',
                name: values.linked_special_pay_config?.label || '',
                link_date: dayjs().utc().toISOString(),
                link_type: values.link_type,
              },
            ]
          : [],
      }

      // If editing, include the accessorial ID
      if (editingAccessorial) {
        processedFormData._id = editingAccessorial._id
      }

      await createAccessorialMutation.mutateAsync(processedFormData)
      // Reset form and trigger success callback
      form.resetFields()
      onSuccess()
    } catch (error) {
      console.error('Error creating/updating manual accessorial:', error)
      message.error('Failed to create/update accessorial')
    }
  }

  return (
    <Vertical bg='default' gap={16}>
      <Form
        form={form}
        layout='vertical'
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        onFinish={onFinish}
        initialValues={{
          requires_approval: false,
          requires_reason_code: false,
          exclude_from_invoice: false,
          fee_type: 'Per Order',
          effective_from: dayjs(),
          rate: 0,
          product_group: null,
        }}
      >
        <Vertical gap={16} style={{ overflowY: 'auto' }} flex={1}>
          {/* Basic Details */}
          <Card>
            {editingAccessorial ? (
              // Display mode for editing
              <Horizontal gap={8} flex='1' style={{ overflow: 'visible', marginBottom: 16 }}>
                <Vertical flex='2' gap={4}>
                  <Texto category='label' appearance='hint'>
                    Accessorial Name
                  </Texto>
                  <Texto weight='bold'>{editingAccessorial.name}</Texto>
                </Vertical>
                <Vertical flex='1' gap={4}>
                  <Texto category='label' appearance='hint'>
                    Effective From
                  </Texto>
                  <Texto weight='bold'>
                    {editingAccessorial.rates[0]?.effective_from
                      ? dayjs(editingAccessorial.rates[0]?.effective_from as string).format('MM/DD/YYYY')
                      : 'N/A'}
                  </Texto>
                </Vertical>
              </Horizontal>
            ) : (
              <>
                <Form.Item name='name' label='Name' rules={[{ required: true, message: 'Name is required' }]}>
                  <Input placeholder='Enter accessorial name' autoFocus />
                </Form.Item>
              </>
            )}
            <Form.Item
              name='description'
              label='Description'
              rules={[{ required: true, message: 'Description is required' }]}
            >
              <Input.TextArea placeholder='Enter description' rows={4} />
            </Form.Item>
          </Card>

          {/* Applies To */}
          <Card title='Applies To'>
            <Vertical gap={16}>
              <Form.Item name='freight_region' label='Freight Region' extra='Leave empty to apply to all regions'>
                <Select
                  allowClear
                  placeholder='Select region'
                  options={metadata?.freight_regions?.map((region: { id: string; name: string }) => ({
                    label: region.name,
                    value: region.name,
                    region_id: region.id,
                  }))}
                  showSearch
                  optionFilterProp='label'
                  onChange={(value, option: { region_id: string } | { region_id: string }[] | undefined) => {
                    if (!Array.isArray(option)) {
                      form.setFieldValue('freight_region_id', value ? option?.region_id : undefined)
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name='freight_region_id' hidden>
                <Input />
              </Form.Item>

              <Form.Item name='product_group' label='Product Group'>
                <Select
                  placeholder='Select Product Group'
                  style={{ width: '100%' }}
                  filterSort={(optionA, optionB) => {
                    const labelA = String(optionA.label || '')
                    const labelB = String(optionB.label || '')
                    return labelA.localeCompare(labelB)
                  }}
                  options={metadata?.product_groups?.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                  allowClear
                  showSearch
                />
              </Form.Item>
            </Vertical>
          </Card>

          {/* Rate & Approval */}
          <Card title='Rate & Approval'>
            <Horizontal gap={16}>
              <Form.Item
                name='fee_type'
                label='Fee Type'
                rules={[{ required: true, message: 'Fee Type is required' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder='Select Fee Type'
                  style={{ width: '100%' }}
                  filterSort={(optionA, optionB) => {
                    const labelA = String(optionA.label || '')
                    const labelB = String(optionB.label || '')
                    return labelA.localeCompare(labelB)
                  }}
                  options={metadata?.ratebook_options?.accessorials?.basic_fee_types
                    ?.filter((type) => !isPercentageFeeType(type))
                    .map((option: string) => ({
                      label: option,
                      value: option,
                    }))}
                  showSearch
                  onChange={() => {
                    form.setFieldsValue({ linked_special_pay_config: undefined })
                  }}
                />
              </Form.Item>

              <Form.Item
                name='rate'
                label='Rate'
                rules={[
                  { required: true, message: 'Rate is required' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (isPercentageFeeType(getFieldValue('fee_type'))) {
                        if (value < 0 || value > 200) {
                          return Promise.reject(new Error('Rate must be between 0 and 200 percent'))
                        }
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
                style={{ flex: 1 }}
              >
                <InputNumber
                  placeholder='Enter Rate'
                  precision={5}
                  max={isPercentageType ? 200 : undefined}
                  addonBefore={isPercentageType ? null : '$'}
                  addonAfter={getFeeTypeUnit(currentFeeType)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Horizontal>

            <Divider style={{ margin: '16px 0 8px 0' }} />

            <Horizontal gap={16} alignItems='center' justifyContent='space-between' style={{ marginTop: 16 }}>
              <Vertical gap={0}>
                <Texto>Require Approval</Texto>
                <Texto appearance='hint' style={{ fontSize: '12px' }}>
                  Accessorial charges will need approval before being applied
                </Texto>
              </Vertical>
              <Form.Item name='requires_approval' valuePropName='checked' style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </Horizontal>

            <Horizontal gap={16} alignItems='center' justifyContent='space-between' style={{ marginTop: 16 }}>
              <Vertical gap={0}>
                <Texto>Require Reason Code</Texto>
                <Texto appearance='hint' style={{ fontSize: '12px' }}>
                  Users must provide a reason when applying this accessorial
                </Texto>
              </Vertical>
              <Form.Item name='requires_reason_code' valuePropName='checked' style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </Horizontal>

            <Horizontal gap={16} alignItems='center' justifyContent='space-between' style={{ marginTop: 16 }}>
              <Vertical gap={0}>
                <Texto>Exclude From Invoice</Texto>
                <Texto appearance='hint' style={{ fontSize: '12px' }}>
                  This accessorial will not appear on customer invoices
                </Texto>
              </Vertical>
              <Form.Item name='exclude_from_invoice' valuePropName='checked' style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </Horizontal>
            <Horizontal gap={16} alignItems='center' justifyContent='space-between' style={{ marginTop: 16 }}>
              <Vertical gap={0}>
                <Texto>Link to Driver Special Pay</Texto>
                <Texto appearance='hint' style={{ fontSize: '12px' }}>
                  Automatically trigger driver special pay when this accessorial is applied{' '}
                </Texto>
              </Vertical>
              <Form.Item name='link_to_special_pay' valuePropName='checked' style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </Horizontal>
            <FreightLinkingOptionsSelect
              form={form}
              specialPayOptions={specialPayOptions}
              bookType={ratebook.book_type}
              isManual
            />
          </Card>
        </Vertical>

        <Horizontal bg='elevated' flex='0' className='p-3' style={{ overflow: 'visible' }} alignItems='center' gap={8}>
          <Horizontal className='px-2' verticalCenter gap={8} flex={1}>
            <Texto weight='bold'>Effective From: </Texto>
            <Form.Item
              name='effective_from'
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: 'Effective From date is required' }]}
            >
              <DatePicker format='YYYY-MM-DD' style={{ width: '180px' }} />
            </Form.Item>
          </Horizontal>

          <Horizontal gap={8}>
            <GraviButton onClick={onCancel}>Cancel</GraviButton>
            <Form.Item label={null} style={{ marginBottom: 0 }}>
              <Button loading={createAccessorialMutation.isPending} htmlType='submit' type='primary'>
                {editingAccessorial ? 'Update Accessorial' : 'Create Accessorial'}
              </Button>
            </Form.Item>
          </Horizontal>
        </Horizontal>
      </Form>
    </Vertical>
  )
}
